/**
 * Acceptable Use Policy (AUP) Modal
 * Displayed on first login - users must accept before accessing the system.
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Shield, AlertTriangle } from 'lucide-react';

const AUP_CONTENT = `
## CareVaultPro: Acceptable Use Policy (AUP)

### 1. Purpose & Scope
This policy governs the use of CareVaultPro (CVP) by all employees and contractors of your organization. By accessing this system, you agree to comply with HIPAA, M.G.L. c. 93H, and your organization's internal security protocols.

### 2. Access & Identity

**Unique Login:** You may only access CVP using your assigned work email.

**Credential Protection:** Sharing passwords or using another staff member's login is strictly prohibited.

**MFA Requirement:** You must maintain active Multi-Factor Authentication as configured during your profile setup.

### 3. Data Security & HIPAA Compliance

**Minimum Necessary:** You shall only access the Client/Patient records for whom you have a direct clinical or administrative "need to know."

**Authorized Export & Printing:** You are permitted to download or export Client/Patient records only when required for authorized clinical purposes (e.g., printing a form for a "wet" signature, providing a copy to a Client/Patient, or fulfilling a legal request).

**Secure Transmission:** Any exported files containing PHI must be handled with extreme care. Use the CVP Client Portal for secure communication whenever possible.

### 4. Remote Work & Device Safety

**Public Wi-Fi:** Accessing CVP from unsecured public Wi-Fi is prohibited unless using an agency-approved VPN.

**Screen Privacy:** When working in public or shared spaces, ensure your screen is not visible to unauthorized persons.

**Auto-Lock:** You agree to keep "Auto-Lock" enabled on any device used to access CVP. Do not leave a logged-in device unattended.

### 5. Clinical Documentation Integrity

**Legal Signature:** You acknowledge that your electronic signature within CVP is the legal equivalent of your handwritten signature.

**Audit Trail:** You understand that every action—including viewing, editing, downloading, or exporting a Client/Patient record—is logged in a permanent, immutable audit trail.

### 6. Reporting Requirements

You must immediately report any lost/stolen devices or suspected security breaches to the Data Security Coordinator.
`;

export const AUPModal = ({ isOpen, onAccept, isLoading }) => {
  const [hasRead, setHasRead] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);

  const canAccept = hasRead && hasAgreed;

  // Simple markdown-like rendering
  const renderContent = (text) => {
    return text.split('\n').map((line, index) => {
      // Headers
      if (line.startsWith('### ')) {
        return <h3 key={index} className="text-lg font-semibold mt-6 mb-2 text-gray-900">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={index} className="text-xl font-bold mt-4 mb-3 text-teal-700">{line.replace('## ', '')}</h2>;
      }
      
      // Bold text within paragraphs
      if (line.includes('**')) {
        const parts = line.split('**');
        return (
          <p key={index} className="mb-2 text-gray-700">
            {parts.map((part, i) => 
              i % 2 === 1 ? <strong key={i} className="text-gray-900">{part}</strong> : part
            )}
          </p>
        );
      }
      
      // Empty lines
      if (line.trim() === '') {
        return <div key={index} className="h-2" />;
      }
      
      // Regular paragraphs
      return <p key={index} className="mb-2 text-gray-700">{line}</p>;
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-3xl p-0 overflow-hidden"
        style={{ maxHeight: '85vh' }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Shield className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Acceptable Use Policy</DialogTitle>
              <p className="text-sm text-gray-500 mt-1">Please review and accept before continuing</p>
            </div>
          </div>
        </DialogHeader>
        
        <div className="px-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              You must read and accept this policy to access CareVaultPro. Your acceptance is logged for compliance purposes.
            </p>
          </div>
        </div>

        <div 
          className="mx-6 mt-4 border rounded-lg bg-white shadow-inner"
          style={{ 
            height: '280px', 
            overflowY: 'scroll',
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 #f1f1f1'
          }}
          data-testid="aup-content-scroll"
        >
          <div className="p-4">
            {renderContent(AUP_CONTENT)}
            <div className="mt-4 pt-4 border-t border-gray-300 text-center text-sm text-gray-500">
              — End of Policy —
            </div>
          </div>
        </div>
        
        <p className="text-xs text-gray-500 text-center mt-2 px-6">
          ↓ Scroll down to read the complete policy ↓
        </p>

        <div className="p-6 pt-4 border-t bg-gray-50 space-y-4 flex-shrink-0">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="hasRead"
                checked={hasRead}
                onCheckedChange={setHasRead}
                data-testid="aup-read-checkbox"
              />
              <label 
                htmlFor="hasRead" 
                className="text-sm text-gray-700 cursor-pointer leading-tight"
              >
                I confirm that I have read and understand the Acceptable Use Policy in its entirety.
              </label>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox
                id="hasAgreed"
                checked={hasAgreed}
                onCheckedChange={setHasAgreed}
                data-testid="aup-agree-checkbox"
              />
              <label 
                htmlFor="hasAgreed" 
                className="text-sm text-gray-700 cursor-pointer leading-tight"
              >
                I agree to comply with all terms outlined in this policy and understand that violations may result in disciplinary action, including termination of access.
              </label>
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              onClick={onAccept}
              disabled={!canAccept || isLoading}
              className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700"
              data-testid="aup-accept-button"
            >
              {isLoading ? 'Processing...' : 'I Accept the Acceptable Use Policy'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AUPModal;
